import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import {
  Code2, Check, Clock, ChevronDown, AlertCircle, Package, Info, ListChecks, X, Loader2,
} from 'lucide-react';
import { useTheme } from '../../../hooks/useTheme';

const DIFF_CHIP = {
  Easy: 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/20',
  Medium: 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-500/20',
  Hard: 'bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-200 dark:border-rose-500/20',
  Custom: 'bg-violet-50 dark:bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-200 dark:border-violet-500/20',
};

const DiffBadge = ({ difficulty }) => (
  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border flex-shrink-0 ${DIFF_CHIP[difficulty] || DIFF_CHIP.Custom}`}>
    {difficulty || '—'}
  </span>
);

// Tiny inline "info" affordance — reuses the native title tooltip already used
// elsewhere in this file rather than introducing a new tooltip component.
const InfoHint = ({ text }) => (
  <span tabIndex={0} title={text} className="inline-flex text-gray-300 dark:text-gray-600 hover:text-violet-400 dark:hover:text-violet-400 transition-colors cursor-help">
    <Info size={11} />
  </span>
);

const CodingRoundConfig = ({
  isCodingIncluded,
  selectedCodingSet,
  codingSets = [],
  codingSetsData = {},
  codingSetProblems = [],
  selfHostedProblemIds = new Set(),
  isFetchingSetProblems = false,
  codingEnabled = true,
  error = '',
  onToggle,
  onPreviewSet,
  onSave,
  onViewProblem,
}) => {
  const { theme } = useTheme();
  const [modalOpen, setModalOpen] = useState(false);
  const [draftSet, setDraftSet] = useState('');
  const [setDropdownOpen, setSetDropdownOpen] = useState(false);
  const setDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (setDropdownRef.current && !setDropdownRef.current.contains(e.target)) {
        setSetDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Modal-opening is driven directly by these click handlers (never by an effect
  // watching isCodingIncluded) — that prop can also flip true→false when editing
  // a JD loads its saved data, and that data-load must not pop the modal open.
  const handleRowClick = () => {
    if (!codingEnabled) return;
    if (!isCodingIncluded) {
      onToggle();
      setDraftSet('');
      setModalOpen(true);
      return;
    }
    setDraftSet(selectedCodingSet && selectedCodingSet !== 'NA' ? selectedCodingSet : '');
    setModalOpen(true);
  };

  const handleSwitchClick = (e) => {
    e.stopPropagation();
    const turningOn = !isCodingIncluded;
    onToggle();
    if (turningOn) {
      setDraftSet('');
      setModalOpen(true);
    }
  };

  const handleDraftSetChange = (setName) => {
    setDraftSet(setName);
    setSetDropdownOpen(false);
    onPreviewSet?.(setName);
  };

  const handleSave = () => {
    onSave?.(draftSet);
    setModalOpen(false);
  };

  const isSetChosen = Boolean(selectedCodingSet) && selectedCodingSet !== 'NA';
  const setMeta = codingSetsData[selectedCodingSet];
  const questionSlots = setMeta?.question_slots || [];
  const languages = setMeta?.allowed_languages || [];
  const timeLimit = setMeta?.time_limit_minutes;
  const isApexSet = setMeta?.track === 'apex';
  const totalQuestions = questionSlots.filter((s) => (s.problem_ids || []).length > 0).length;

  // Modal preview reflects whichever set is currently being drafted/browsed.
  const draftMeta = codingSetsData[draftSet];
  const draftQuestionSlots = draftMeta?.question_slots || [];
  const draftLanguages = draftMeta?.allowed_languages || [];
  const draftTimeLimit = draftMeta?.time_limit_minutes;
  const isDraftApex = draftMeta?.track === 'apex';
  const draftTotalQuestions = draftQuestionSlots.filter((s) => (s.problem_ids || []).length > 0).length;

  const problemsById = useMemo(() => {
    const map = {};
    codingSetProblems.forEach((p) => { map[p.problem_id] = p; });
    return map;
  }, [codingSetProblems]);

  // Summary chips shown in collapsed header
  const showSummaryChips = isCodingIncluded && codingEnabled && isSetChosen;

  return (
    <>
      <div className={`rounded-2xl border transition-all ${!codingEnabled ? 'opacity-60' : ''} ${error ? 'border-red-500' : isCodingIncluded && codingEnabled ? 'border-violet-200 dark:border-violet-500/30' : 'border-gray-200 dark:border-white/[0.08]'}`}>

        {/* ── Section Header ── */}
        <div
          className={`flex items-center gap-3 px-4 py-3.5 ${codingEnabled ? 'cursor-pointer select-none' : 'cursor-not-allowed'}`}
          onClick={handleRowClick}
        >
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${isCodingIncluded && codingEnabled ? 'bg-violet-100 dark:bg-violet-500/20 text-violet-600 dark:text-violet-400' : 'bg-gray-100 dark:bg-white/[0.06] text-gray-400 dark:text-gray-500'}`}>
            <Code2 size={17} />
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">Coding Round</p>
            {showSummaryChips ? (
              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                <span className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border border-violet-200 dark:border-violet-500/30 bg-violet-50 dark:bg-violet-500/10 text-violet-600 dark:text-violet-400">
                  {totalQuestions}Q
                </span>
                {isApexSet && (
                  <span className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border border-sky-200 dark:border-sky-500/30 bg-sky-50 dark:bg-sky-500/10 text-sky-600 dark:text-sky-400">
                    Apex · AI-reviewed
                  </span>
                )}
                {Number(timeLimit) > 0 && (
                  <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-2 py-0.5 rounded-full border border-gray-200 dark:border-white/[0.12] bg-gray-50 dark:bg-white/[0.04] text-gray-500 dark:text-gray-400">
                    <Clock size={9} />
                    {timeLimit} min
                  </span>
                )}
                <span className="text-[10px] text-gray-400 dark:text-gray-500">· Tap to change</span>
              </div>
            ) : isCodingIncluded && codingEnabled ? (
              <p className={`text-[11px] mt-0.5 ${error ? 'text-red-500 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}>
                {error || 'Tap to choose a problem set'}
              </p>
            ) : (
              <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">Test problem-solving with real coding challenges</p>
            )}
          </div>

          {!codingEnabled ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-pink-500/20 to-blue-500/20 border border-pink-500/30 dark:text-pink-300 text-pink-600 flex-shrink-0">
              ✦ Upgrade Plan
            </span>
          ) : (
            <button
              type="button"
              role="switch"
              aria-checked={isCodingIncluded}
              aria-label="Include Coding Round"
              onClick={handleSwitchClick}
              className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 focus:outline-none ${isCodingIncluded ? 'bg-violet-500' : 'bg-gray-200 dark:bg-white/10'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${isCodingIncluded ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          )}
        </div>
      </div>

      {/* ── Popup: choose a set, preview it, save ── */}
      {/* Portalled straight to <body> so the fixed overlay is never clipped or
          confined by a transformed/scrolling ancestor — always covers the full viewport. */}
      {modalOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-bgPrimary/50 dark:bg-black/20"
            style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
            onClick={() => setModalOpen(false)}
          />

          {/* Modal card */}
          <div
            className="w-full max-w-2xl max-h-[88vh] flex flex-col relative bg-bgSecondary rounded-2xl shadow-2xl border border-gray-200 dark:border-white/10 animate-[fadeInScale_0.2s_ease-out]"
            style={{ background: theme === 'dark' ? 'linear-gradient(to bottom right, rgba(15,18,42,0.94), rgba(10,9,28,0.90))' : '' }}
          >

            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-white/[0.07] flex-shrink-0">
              <p className="text-sm font-medium dark:text-white text-textPrimary">Configure Coding Round</p>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-rose-500 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-all flex-shrink-0"
              >
                <X size={16} />
              </button>
            </div>

            {/* Problem Set Picker — kept outside the scrollable body below so its
                dropdown popup never gets clipped by that container's overflow-y-auto. */}
            <div className="px-5 pt-4 pb-2 flex-shrink-0">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 flex items-center gap-1.5 mb-2">
                  <Package size={11} />
                  Problem Set <span className="text-red-400 normal-case font-normal">*</span>
                  <InfoHint text="A ready-made bundle of coding questions built in the Test Library. Pick one to see exactly what candidates will get." />
                </label>
                <div className="relative" ref={setDropdownRef}>
                  <button
                    type="button"
                    aria-haspopup="listbox"
                    aria-expanded={setDropdownOpen}
                    onClick={() => setSetDropdownOpen((prev) => !prev)}
                    className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border text-sm text-left transition-all dark:glass-effect bg-bgPrimary ${setDropdownOpen ? 'border-violet-400 dark:border-violet-500/60 ring-1 ring-violet-400/30' : 'border-gray-200 dark:border-white/[0.10]'}`}
                  >
                    <Package size={14} className={draftSet ? 'text-violet-500 dark:text-violet-400 flex-shrink-0' : 'text-gray-400 flex-shrink-0'} />
                    <span className={`flex-1 truncate ${draftSet ? 'text-gray-800 dark:text-gray-200 font-medium' : 'text-gray-400 dark:text-gray-500 italic'}`}>
                      {draftSet || 'Choose a problem set…'}
                    </span>
                    <ChevronDown size={14} className={`text-gray-400 flex-shrink-0 transition-transform duration-200 ${setDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {setDropdownOpen && (
                    <div role="listbox" className="absolute z-20 mt-1.5 w-full rounded-xl border border-gray-200 dark:border-white/[0.10] bg-white dark:bg-[#0a1733] shadow-xl overflow-hidden">
                      {codingSets.length === 0 ? (
                        <div className="px-4 py-5 text-center">
                          <p className="text-xs text-gray-400 dark:text-gray-500">No problem sets available</p>
                        </div>
                      ) : (
                        <div className="max-h-60 overflow-y-auto">
                          {codingSets.map((set, i) => (
                            <button
                              key={i}
                              type="button"
                              role="option"
                              aria-selected={draftSet === set}
                              onClick={() => handleDraftSetChange(set)}
                              className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-left transition-colors ${draftSet === set ? 'bg-violet-50 dark:bg-violet-500/10 text-violet-700 dark:text-violet-300 font-medium' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.05]'}`}
                            >
                              <Package size={13} className={draftSet === set ? 'text-violet-500 flex-shrink-0' : 'text-gray-400 flex-shrink-0'} />
                              <span className="flex-1 truncate">{set}</span>
                              {draftSet === set && <Check size={13} className="text-violet-500 flex-shrink-0" />}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Scrollable preview area */}
            <div className="flex-1 overflow-y-auto px-5 pt-2 pb-4">
              {/* Reserves room so the dropdown popup (which can be up to ~240px tall)
                  never has to overlap the footer buttons while nothing is picked yet. */}
              {!draftSet && (
                <div className="min-h-[260px] flex flex-col items-center justify-center gap-1.5 text-center">
                  <Package size={20} className="text-gray-300 dark:text-white/20" />
                  <p className="text-xs text-gray-400 dark:text-gray-500">Select a problem set to preview it here</p>
                </div>
              )}
              {/* Read-only preview of the drafted set */}
              {draftSet && (
                <div className="space-y-4">
                  {isFetchingSetProblems ? (
                    <div className="space-y-2">
                      {[1, 2, 3].map((i) => <div key={i} className="h-14 rounded-xl bg-gray-100 dark:bg-white/[0.06] animate-pulse" />)}
                    </div>
                  ) : (
                    <>
                      {/* Summary */}
                      <div className="rounded-xl border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-white/[0.02] p-4">
                        <div className="grid grid-cols-3 gap-4">
                          <div className="space-y-1 min-w-0">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Languages</p>
                            <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">{draftLanguages.join(', ') || '—'}</p>
                          </div>
                          <div className="space-y-1">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Total Questions</p>
                            <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">{draftTotalQuestions}</p>
                          </div>
                          <div className="space-y-1">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Time Limit</p>
                            <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">{draftTimeLimit ? `${draftTimeLimit} min` : '—'}</p>
                          </div>
                        </div>
                      </div>

                      {isDraftApex && (
                        <div className="flex items-start gap-2 px-3.5 py-2.5 rounded-xl bg-sky-50 dark:bg-sky-500/10 border border-sky-100 dark:border-sky-500/20">
                          <Info size={13} className="flex-shrink-0 mt-0.5 text-sky-500 dark:text-sky-400" />
                          <p className="text-xs text-sky-700 dark:text-sky-300">
                            Salesforce Apex set. Candidates write Apex in the editor and answers are scored by AI against each question's rubric. Code is not executed.
                          </p>
                        </div>
                      )}

                      {/* Question-wise breakdown */}
                      <div className="space-y-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 flex items-center gap-1.5">
                          <ListChecks size={11} />
                          Questions
                        </p>
                        {draftQuestionSlots.length === 0 ? (
                          <div className="flex flex-col items-center justify-center py-6 gap-1.5 rounded-xl border border-dashed border-gray-200 dark:border-white/[0.08]">
                            <AlertCircle size={16} className="text-gray-300 dark:text-white/20" />
                            <p className="text-xs text-gray-400 dark:text-gray-500">This set has no questions yet</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {draftQuestionSlots.map((slot, idx) => {
                              const slotProblems = (slot.problem_ids || []).map((pid) => problemsById[pid]).filter(Boolean);
                              return (
                                <div key={slot.id || idx} className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/[0.02] overflow-hidden">
                                  <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-gray-100 dark:border-white/[0.07]">
                                    <p className="text-xs font-semibold text-gray-800 dark:text-gray-200">Question {idx + 1}</p>
                                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 dark:bg-white/[0.08] text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-white/10">
                                      {slotProblems.length} {slotProblems.length === 1 ? 'problem' : 'problems'}
                                    </span>
                                  </div>
                                  <div className="p-2 space-y-1.5">
                                    {slotProblems.map((problem) => (
                                      <button
                                        key={problem.problem_id}
                                        type="button"
                                        onClick={() => onViewProblem?.(problem)}
                                        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg bg-gray-50/60 dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.07] hover:border-gray-200 dark:hover:border-white/[0.15] transition-colors text-left"
                                      >
                                        <DiffBadge difficulty={selfHostedProblemIds.has(problem.problem_id) ? 'Custom' : problem.difficulty} />
                                        <span className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">{problem.title}</span>
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-100 dark:border-white/[0.07] flex-shrink-0">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="border-btn border-btn-dark !text-black dark:!text-white px-5 py-2.5 rounded-full text-sm font-medium transition-all"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!draftSet}
                className="button flex items-center gap-2 px-6 py-2.5 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {isFetchingSetProblems && <Loader2 size={14} className="animate-spin" />}
                <span>Save</span>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default CodingRoundConfig;
